// frontend/src/components/ProductBarcodesList.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { 
  Edit, Trash2, Plus, Building, User, Barcode, Package, 
  Filter, Search, ChevronDown, AlertCircle, Loader2, 
  Eye, Globe, Scale, CheckCircle, RefreshCw
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import "./ProductBarcodesList.css";

const url = process.env.REACT_APP_BACKEND_URL;

const ProductBarcodesList = () => {
  const { currentUser, userType, company } = useAuth();
  const navigate = useNavigate();
  
  const [barcodes, setBarcodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [companies, setCompanies] = useState([]);
  const [products, setProducts] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [activeOnly, setActiveOnly] = useState(true);
  
  // For system admins
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const isSystemAdmin = userType === 'system' && (currentUser?.role === 'developer' || currentUser?.role === 'admin');

  useEffect(() => {
    fetchBarcodes();
    
    // System admins can filter by company
    if (isSystemAdmin) {
      fetchCompanies();
    }
    
    // Fetch products for filtering
    fetchProducts();
  }, [selectedCompany, selectedProduct]);

  const fetchBarcodes = async () => {
    try {
      setLoading(true);
      
      let endpoint = `${url}/api/barcode`;
      const params = {};
      
      // System admins can filter by company
      if (isSystemAdmin && selectedCompany) {
        params.company_id = selectedCompany;
      }
      
      // Filter by product
      if (selectedProduct) {
        params.product_id = selectedProduct;
      }
      
      // Filter by active status
      if (activeOnly) {
        params.is_active = 'true';
      }
      
      const response = await axios.get(endpoint, { params });
      
      if (response.data.success) {
        setBarcodes(response.data.data || []);
      } else {
        toast.error(response.data.message || "Failed to fetch barcodes");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to fetch barcodes");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllBarcodes = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${url}/api/barcode/all`);
      
      if (response.data.success) {
        setBarcodes(response.data.data || []);
      } else {
        toast.error(response.data.message || "Failed to fetch all barcodes");
      }
    } catch (error) {
      toast.error("Failed to fetch all barcodes");
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      setLoadingCompanies(true);
      const response = await axios.get(`${url}/api/company`);
      
      if (response.data.success) {
        setCompanies(response.data.data || []);
      }
    } catch (error) {
      toast.error("Failed to load companies");
    } finally {
      setLoadingCompanies(false);
    }
  };

  const fetchProducts = async () => {
    try {
      setLoadingProducts(true);
      let endpoint = `${url}/api/products`;
      
      if (isSystemAdmin && selectedCompany) {
        endpoint = `${url}/api/products/all`;
      }
      
      const response = await axios.get(endpoint, {
        params: selectedCompany && isSystemAdmin ? { company_id: selectedCompany } : {}
      });
      
      if (response.data.success) {
        setProducts(response.data.data || []);
      }
    } catch (error) {
      // Silent fail
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleDelete = async (id, barcodeValue) => {
    if (!window.confirm(`Are you sure you want to delete barcode "${barcodeValue}"? This will set it to inactive.`)) {
      return;
    }
    
    try {
      const response = await axios.delete(`${url}/api/barcode/${id}`);
      
      if (response.data.success) {
        toast.success(`Barcode "${barcodeValue}" deleted successfully`);
        fetchBarcodes();
      } else {
        toast.error(response.data.message || "Failed to delete barcode");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete barcode");
    }
  };

  const handleSetPrimary = async (barcodeId, barcodeValue) => {
    if (!window.confirm(`Set "${barcodeValue}" as primary barcode for this product?`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${url}/api/barcode/set-primary`, 
        { barcode_id: barcodeId }
      );
      
      if (response.data.success) {
        toast.success("Primary barcode set successfully");
        fetchBarcodes();
      } else {
        toast.error(response.data.message || "Failed to set primary barcode");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to set primary barcode");
    }
  };

  const handleViewBarcode = (barcode) => {
    const details = `
Product Barcode Details:

Barcode: ${barcode.barcode}
Barcode ID: ${barcode.barcode_id || "N/A"}
Product: ${barcode.product_id?.product_name || "N/A"} (${barcode.product_id?.product_code || "N/A"})
UOM: ${barcode.uom_id?.uom_name || "N/A"} (${barcode.uom_id?.uom_type || "N/A"})
Company: ${barcode.company_id?.company_name || "N/A"}
Primary: ${barcode.is_primary ? 'Yes' : 'No'}
Status: ${barcode.is_active ? 'Active' : 'Inactive'}
Created By: ${barcode.created_by?.name || barcode.created_by?.Usercomp_sure_name || "System"}
Created: ${new Date(barcode.created_at).toLocaleDateString()}
    `;
    alert(details);
  };

  const handleEditBarcode = (id) => {
    navigate(`/barcode/edit/${id}`);
  };

  // Filter barcodes based on search term
  const filteredBarcodes = barcodes.filter(barcode =>
    barcode.barcode.toLowerCase().includes(searchTerm.toLowerCase()) ||
    barcode.product_id?.product_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    barcode.product_id?.product_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    barcode.uom_id?.uom_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    barcode.company_id?.company_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate stats
  const totalBarcodes = barcodes.length;
  const primaryBarcodes = barcodes.filter(b => b.is_primary).length;
  const activeBarcodes = barcodes.filter(b => b.is_active).length;

  if (loading) {
    return (
      <div className="loading-container">
        <Loader2 className="animate-spin" size={32} />
        <p>Loading product barcodes...</p>
      </div>
    );
  }

  return (
    <div className="barcodes-container">
      {/* Header */}
      <div className="header">
        <div>
          <h2>Product Barcodes</h2>
          <p className="company-info">
            {isSystemAdmin ? (
              <>
                <Globe size={14} /> All Companies
              </>
            ) : (
              <>
                <Building size={14} /> {company?.company_name} ({company?.company_code})
              </>
            )}
          </p>
        </div>
        
        <div className="header-actions">
          {/* Filter Toggle */}
          <button 
            className="filter-btn"
            onClick={() => setShowFilters(!showFilters)}
          >
            <Filter size={16} />
            Filter
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-container">
        <div className="stat-card">
          <div className="stat-icon">
            <Barcode size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{totalBarcodes}</div>
            <div className="stat-label">Total Barcodes</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon primary">
            <CheckCircle size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{primaryBarcodes}</div>
            <div className="stat-label">Primary</div>
          </div>
        </div>
        
        <div className="stat-card">
          <div className="stat-icon active">
            <CheckCircle size={20} />
          </div>
          <div className="stat-content">
            <div className="stat-number">{activeBarcodes}</div>
            <div className="stat-label">Active</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="filters-container">
          <div className="filter-group">
            <label>Filter by Company:</label>
            <div className="select-wrapper">
              <select
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                disabled={loadingCompanies || !isSystemAdmin}
              >
                <option value="">All Companies</option>
                {companies.map((comp) => (
                  <option key={comp._id} value={comp._id}>
                    {comp.company_name} ({comp.company_code})
                  </option>
                ))}
              </select>
              <Building size={16} className="select-icon" />
            </div>
          </div>
          
          <div className="filter-group">
            <label>Filter by Product:</label>
            <div className="select-wrapper">
              <select
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                disabled={loadingProducts}
              >
                <option value="">All Products</option>
                {products.map((product) => (
                  <option key={product._id} value={product._id}>
                    {product.product_name} ({product.product_code})
                  </option>
                ))}
              </select>
              <Package size={16} className="select-icon" />
            </div>
          </div>
          
          <div className="filter-group checkbox-group">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={(e) => setActiveOnly(e.target.checked)}
              />
              <span>Show Active Only</span>
            </label>
          </div>
          
          <div className="filter-actions">
            <button 
              className="clear-filters"
              onClick={() => {
                setSelectedCompany("");
                setSelectedProduct("");
                setActiveOnly(true);
              }}
            >
              Clear Filters
            </button>
            
            <button 
              className="apply-filters"
              onClick={() => {
                fetchBarcodes();
                setShowFilters(false);
              }}
            >
              Apply Filters
            </button>
          </div>
        </div>
      )}

      {/* Search Bar */}
      <div className="search-container">
        <div className="search-box">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search barcodes, products, or UOMs..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <button 
              className="clear-search"
              onClick={() => setSearchTerm("")}
            >
              ✕
            </button>
          )}
        </div>
        
        <div className="search-actions">
          <button 
            className="refresh-btn"
            onClick={isSystemAdmin ? fetchAllBarcodes : fetchBarcodes}
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
          
          <div className="stats">
            <span>Showing: {filteredBarcodes.length} of {totalBarcodes} barcodes</span>
            {(selectedCompany || selectedProduct) && (
              <span className="filter-badge">
                <Filter size={12} />
                Filtered
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Barcodes Table */}
      <div className="table-container">
        <table className="styled-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Barcode</th>
              <th>Product</th>
              <th>UOM</th>
              {isSystemAdmin && <th>Company</th>}
              <th>Created By</th>
              <th>Created Date</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {filteredBarcodes.length === 0 ? (
              <tr>
                <td colSpan={isSystemAdmin ? 9 : 8} className="no-data">
                  <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Barcode size={48} color="#ccc" />
                    <p style={{ margin: '15px 0', color: '#666' }}>
                      {searchTerm || selectedCompany || selectedProduct
                        ? "No barcodes found matching your criteria"
                        : "No barcodes found"
                      }
                    </p>
                    <button 
                      className="add-button" 
                      onClick={() => navigate("/AddProductBarcode")}
                    >
                      <Plus size={16} /> Create First Barcode
                    </button>
                  </div>
                </td>
              </tr>
            ) : (
              filteredBarcodes.map((barcode, index) => (
                <tr key={barcode._id} className={barcode.is_primary ? "primary-row" : ""}>
                  <td>{index + 1}</td>
                  
                  <td>
                    <div className="barcode-cell">
                      <Barcode size={14} className="mr-2" color={barcode.is_primary ? "#10b981" : "#667eea"} />
                      <div>
                        <strong>{barcode.barcode}</strong>
                        {barcode.is_primary && (
                          <span className="primary-badge">
                            <CheckCircle size={10} /> Primary
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  
                  <td>
                    <div className="product-cell">
                      <Package size={14} className="mr-2" />
                      <div>
                        <div><strong>{barcode.product_id?.product_name || "N/A"}</strong></div>
                        <small>{barcode.product_id?.product_code || ""}</small>
                      </div>
                    </div>
                  </td>
                  
                  <td>
                    <div className="uom-cell">
                      <Scale size={12} className="mr-2" />
                      <span>{barcode.uom_id?.uom_name || "N/A"}</span>
                    </div>
                  </td>
                  
                  {isSystemAdmin && (
                    <td>
                      <div className="company-cell">
                        <Building size={12} className="mr-2" />
                        <div>
                          <div>{barcode.company_id?.company_name || "N/A"}</div>
                          <small>{barcode.company_id?.company_code || ""}</small>
                        </div>
                      </div>
                    </td>
                  )}
                  
                  <td>
                    <div className="user-cell">
                      <User size={12} className="mr-2" />
                      <div>
                        <div>{barcode.created_by?.name || barcode.created_by?.Usercomp_sure_name || "System"}</div>
                        <small>{barcode.created_by?.email || barcode.created_by?.Usercomp_email || ""}</small>
                      </div>
                    </div>
                  </td>
                  
                  <td>{new Date(barcode.created_at).toLocaleDateString()}</td>
                  
                  <td>
                    <span className={`status-badge ${barcode.is_active ? 'active' : 'inactive'}`}>
                      {barcode.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProductBarcodesList;